import React, { forwardRef, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  TextField,
  Typography,
} from "@mui/material";

const FilterListbox = forwardRef(function FilterListbox(props, ref) {
  const { children, onSelectAll, onDeselectAll, disableSelectAll, disableDeselectAll, ...rest } =
    props;
  return (
    <ul {...rest} ref={ref} style={{ ...rest.style, paddingTop: 0 }}>
      <Box
        component="li"
        sx={{
          listStyle: "none",
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          gap: 1,
          px: 1,
          py: 0.5,
        }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <Button
          size="small"
          onClick={onSelectAll}
          disabled={disableSelectAll}
          sx={{ textTransform: "none", minWidth: 0, px: 1 }}
        >
          Select all
        </Button>
        <Button
          size="small"
          onClick={onDeselectAll}
          disabled={disableDeselectAll}
          sx={{ textTransform: "none", minWidth: 0, px: 1 }}
        >
          Deselect all
        </Button>
      </Box>
      {children}
    </ul>
  );
});

/**
 * Searchable multi-select filter with Select all / Deselect all.
 * value is an array of option values (strings). Empty = no filter.
 */
export const FilterMultiSelect = ({
  label,
  options = [],
  value = [],
  onChange,
  minWidth = 200,
}) => {
  const [inputValue, setInputValue] = useState("");

  const selectedOptions = useMemo(
    () => options.filter((opt) => value.includes(opt.value)),
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (opt) =>
        String(opt.label).toLowerCase().includes(q) ||
        String(opt.value).toLowerCase().includes(q)
    );
  }, [options, inputValue]);

  const handleSelectAll = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const visibleValues = filteredOptions.map((opt) => opt.value);
    onChange(Array.from(new Set([...value, ...visibleValues])));
  };

  const handleDeselectAll = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!inputValue.trim()) {
      onChange([]);
      return;
    }
    const visibleSet = new Set(filteredOptions.map((opt) => opt.value));
    onChange(value.filter((v) => !visibleSet.has(v)));
  };

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      size="small"
      options={options}
      value={selectedOptions}
      inputValue={inputValue}
      onInputChange={(_e, newInput, reason) => {
        if (reason === "reset") return;
        setInputValue(newInput);
      }}
      onChange={(_e, newValue) => {
        onChange(newValue.map((opt) => opt.value));
      }}
      getOptionLabel={(opt) => opt.label || opt.value || ""}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      filterOptions={(opts, state) => {
        const q = (state.inputValue || "").trim().toLowerCase();
        if (!q) return opts;
        return opts.filter(
          (opt) =>
            String(opt.label).toLowerCase().includes(q) ||
            String(opt.value).toLowerCase().includes(q)
        );
      }}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option.value}>
          <Checkbox
            size="small"
            checked={selected}
            sx={{ mr: 1, p: 0.25, "& .MuiSvgIcon-root": { fontSize: 18 } }}
          />
          <Typography variant="body2" noWrap>
            {option.label}
          </Typography>
        </li>
      )}
      renderTags={(tagValue) => {
        if (tagValue.length === 0) return null;
        if (tagValue.length === 1) {
          return (
            <Typography variant="body2" noWrap sx={{ maxWidth: 110, ml: 0.5 }}>
              {tagValue[0].label}
            </Typography>
          );
        }
        return (
          <Typography variant="body2" sx={{ ml: 0.5, whiteSpace: "nowrap" }}>
            {tagValue.length} selected
          </Typography>
        );
      }}
      ListboxProps={{
        onSelectAll: handleSelectAll,
        onDeselectAll: handleDeselectAll,
        disableSelectAll: filteredOptions.length === 0,
        disableDeselectAll: value.length === 0,
      }}
      ListboxComponent={FilterListbox}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length ? "" : "Search…"}
        />
      )}
      sx={{
        minWidth,
        maxWidth: 260,
        "& .MuiAutocomplete-inputRoot": {
          flexWrap: "nowrap",
        },
      }}
    />
  );
};
